import { DEFAULT_SUPPORT_EMAIL } from '../constants/support';

/**
 * Public support address for error copy (mailto target).
 */
export function getSupportEmail() {
  return DEFAULT_SUPPORT_EMAIL;
}

/**
 * Extract a technical message for admins / logging from Error, axios-style, or string.
 */
export function extractTechnicalMessage(error) {
  if (error == null) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    const d = error.response?.data;
    if (d && typeof d.error === 'string') return d.error;
    if (d && d.error && typeof d.error === 'object' && d.error.message) {
      return String(d.error.message);
    }
    if (typeof error.message === 'string' && error.message) return error.message;
  }
  return String(error);
}

function statusToUserMessage(status) {
  if (status === 401) return 'Your session may have expired. Please sign in again.';
  if (status === 403) return "You don't have permission to view this.";
  if (status === 404) return 'The requested information could not be found.';
  if (status === 429) return 'Too many requests. Please wait a few minutes and try again.';
  if (status === 503) return 'The service is temporarily unavailable. Please try again later.';
  return null;
}

/**
 * One paragraph for non-admins: plain language + support contact.
 * Admins see the raw technical message.
 */
export function formatUserFacingError(error, isAdmin) {
  const technical = extractTechnicalMessage(error);
  if (isAdmin) return technical;

  const status =
    (error && typeof error === 'object' && (error.status ?? error.response?.status)) ||
    undefined;
  const hint = statusToUserMessage(status);
  const base =
    hint ||
    'Something went wrong. Please try again in a moment.';
  const email = getSupportEmail();
  return `${base} If the problem continues, contact ${email}.`;
}

/**
 * Log technical details only for admins (e.g. browser console). No-op for other users.
 */
export function logErrorForAdmin(isAdmin, label, error) {
  if (!isAdmin) return;
  const msg = extractTechnicalMessage(error);
  console.error(label, msg, error);
}

/**
 * Log a warning with technical detail only for admins.
 */
export function logWarnForAdmin(isAdmin, label, errorOrMessage) {
  if (!isAdmin) return;
  const msg =
    errorOrMessage && typeof errorOrMessage === 'object'
      ? extractTechnicalMessage(errorOrMessage)
      : String(errorOrMessage);
  console.warn(label, msg);
}

/**
 * General debug/info log only for admins (avoid noisy technical logs for end users).
 */
export function logDebugForAdmin(isAdmin, ...args) {
  if (!isAdmin) return;
  console.log(...args);
}

/**
 * Field 3D terrain: map internal error codes to friendly copy for non-admins.
 */
export function formatTerrainViewerError(error, isAdmin) {
  const technical = extractTechnicalMessage(error);
  if (isAdmin) return technical;
  const m = typeof error === 'object' && error?.message ? error.message : String(error);
  const email = getSupportEmail();
  if (m === 'ASSET_NOT_FOUND') {
    return `Terrain data wasn't found for this view. If the problem continues, contact ${email}.`;
  }
  if (m === 'EE_NOT_READY') {
    return `Terrain isn't ready yet. Please try again in a moment. If the problem continues, contact ${email}.`;
  }
  if (m === 'TIMEOUT') {
    return `The terrain request took too long. Try again. If the problem continues, contact ${email}.`;
  }
  return formatUserFacingError(error, isAdmin);
}
